/**
 * useObstructionHistoryQuery
 *
 * Fetches the timeline events for a single obstruction. Mirrors
 * `useNoteHistoryQuery`.
 *
 * The backend `GET /api/v1/obstructions/{id}/history` endpoint lands in the
 * obstruction-history follow-up (`plan.md` §1.7). Until then the request
 * resolves with 404/501 — the hook simply yields an empty event list so the
 * "Historia" tab renders a friendly empty state instead of throwing.
 */
import { useEffect, useState } from 'react';

import { historyService } from '@/services/api/historyService';
import type { ObstructionHistoryEvent } from '@/types/obstructions';

interface UseObstructionHistoryQueryResult {
  events: ObstructionHistoryEvent[];
  isLoading: boolean;
}

export function useObstructionHistoryQuery(
  obstructionId: string
): UseObstructionHistoryQueryResult {
  const [events, setEvents] = useState<ObstructionHistoryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchHistory(): Promise<void> {
      setIsLoading(true);
      try {
        const response = await historyService.getForObstruction(obstructionId);
        if (!cancelled) setEvents(response.data.events);
      } catch (error) {
        if (!cancelled) {
          setEvents([]);
          console.error(
            '[useObstructionHistoryQuery] failed to load history',
            error
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [obstructionId]);

  return { events, isLoading };
}
